import {
  S3FileLocation,
  S3Method,
  S3RootDirectory
} from "@easycv/common/types/aws.js";
import { Backend } from "@easycv/common/types/util";
import { handleSocketMessage } from "./socketUtilities.js";

type SignedS3UrlResponseError =
  | "Invalid directory."
  | "Invalid filepath."
  | "Invalid method."
  | "refresh"
  | undefined;
type SignedS3UrlResponseSuccess = {
  signedRequest: string;
  url: string;
};
type SignedS3UrlResponse =
  | SignedS3UrlResponseError
  | SignedS3UrlResponseSuccess;
function isSuccessS3Response(x: unknown): x is SignedS3UrlResponseSuccess {
  return (
    typeof x === "object" &&
    Boolean(x) &&
    "signedRequest" in x &&
    "url" in x &&
    typeof x.signedRequest === "string" &&
    typeof x.url === "string"
  );
}

export async function deleteS3Object<T extends S3RootDirectory>(
  backend: Backend,
  location: S3FileLocation<T>
): Promise<void> {
  return new Promise((res, rej) => {
    backend.send("deleteS3Object", location, (data: { success: boolean }) => {
      if (data.success) {
        res();
      } else {
        rej();
      }
    });
  });
}

async function getSignedS3Url<T extends S3RootDirectory>(
  backend: Backend,
  destination: S3FileLocation<T>,
  method: S3Method
): Promise<[socketOk: boolean, response: SignedS3UrlResponse]> {
  const response = await new Promise(
    (res: (_: SignedS3UrlResponse) => void) => {
      backend.send(
        "getSignedS3Url",
        {
          location: destination,
          method
        },
        (data: SignedS3UrlResponse) => res(data)
      );
    }
  );
  return [!handleSocketMessage(response), response];
}

async function testS3FileExists<T extends S3RootDirectory>(
  backend: Backend,
  destination: S3FileLocation<T>
): Promise<[socketOk: boolean, response: boolean]> {
  const response = await new Promise(
    (res: (_: { exists: boolean }) => void) => {
      backend.send(
        "testS3FileExists",
        destination,
        (data: { exists: boolean }) => res(data)
      );
    }
  );
  return [!handleSocketMessage(response), response.exists];
}

export async function Upload<T extends S3RootDirectory>(
  backend: Backend,
  destination: S3FileLocation<T>,
  file: File
) {
  const [socketOk, response] = await getSignedS3Url(
    backend,
    destination,
    "PUT"
  );
  if (socketOk && isSuccessS3Response(response)) {
    return await fetch(response.signedRequest, {
      body: file,
      mode: "cors",
      method: "PUT"
    });
  }
  return Promise.reject();
}

export async function Exists<T extends S3RootDirectory>(
  backend: Backend,
  location: S3FileLocation<T>
): Promise<boolean> {
  const [socketOk, exists] = await testS3FileExists(backend, location);
  if (socketOk) {
    return exists;
  }
  return false;
}

export async function getLink<T extends S3RootDirectory>(
  backend: Backend,
  location: S3FileLocation<T>
): Promise<string> {
  const [socketOk, response] = await getSignedS3Url(backend, location, "GET");
  if (socketOk && isSuccessS3Response(response)) {
    const link = response.signedRequest;
    return link;
  }
}

export async function Download<T extends S3RootDirectory>(
  backend: Backend,
  location: S3FileLocation<T>
) {
  const [socketOk, response] = await getSignedS3Url(backend, location, "GET");
  if (socketOk && isSuccessS3Response(response)) {
    const link = document.createElement("a");
    link.download = location.filename;
    link.target = "_blank";
    link.href = response.signedRequest;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
